/**
 * ========================
 * APP CATALOGUE DOMAIN MENU PROVIDER
 * ========================
 *
 * Menu configuration for the App Catalogue admin domain (mounted at
 * `/admin/app-catalogue/*`). The whole domain is Admin-gated — every section
 * and item uses the same `{ roles: ['Admin'] }` visibility check.
 *
 * Sections:
 *   1. Catalogue    — Overview, Apps
 *   2. Distribution — Distribution, Codes
 *   3. Audience     — Recommendations
 *   4. Access       — Entitlement Preview, Features & Permissions
 *
 * Modelled on `aiMenu.tsx` / `bookingsMenu.tsx`.
 */

import {
  IconLayoutDashboard,
  IconApps,
  IconSparkles,
  IconShieldCheck,
  IconFlag,
  IconDeviceMobile,
  IconTicket,
} from '@tabler/icons-react';
import { DomainMenuProvider, MenuSection, DomainMenuConfig } from '../../layouts/menu/types';
import { createVisibilityCheck } from './BaseMenuProvider';

const ADMIN_ROLES = ['Admin'];

class AppCatalogueMenuProvider implements DomainMenuProvider {
  domainId = 'app-catalogue';

  getMenuConfig(): DomainMenuConfig {
    const admin = createVisibilityCheck({ roles: ADMIN_ROLES });

    const sections: MenuSection[] = [
      {
        id: 'app-catalogue-catalogue',
        label: 'Catalogue',
        visible: admin,
        items: [
          {
            id: 'app-catalogue-overview',
            label: 'Overview',
            path: '/admin/app-catalogue',
            icon: <IconLayoutDashboard size={16} />,
            visible: admin,
          },
          {
            id: 'app-catalogue-apps',
            label: 'Apps',
            path: '/admin/app-catalogue/apps',
            icon: <IconApps size={16} />,
            visible: admin,
          },
        ],
      },
      {
        // CCMS apps are private — they cannot be found by searching a store. This is
        // where an admin says how each one actually reaches a phone, and hands out the
        // Apple redemption codes that are the only way in.
        id: 'app-catalogue-distribution',
        label: 'Distribution',
        visible: admin,
        items: [
          {
            id: 'app-catalogue-distribution-config',
            label: 'Distribution',
            path: '/admin/app-catalogue/distribution',
            icon: <IconDeviceMobile size={16} />,
            visible: admin,
          },
          {
            id: 'app-catalogue-codes',
            label: 'Codes',
            path: '/admin/app-catalogue/codes',
            icon: <IconTicket size={16} />,
            visible: admin,
          },
        ],
      },
      {
        id: 'app-catalogue-audience',
        label: 'Audience',
        visible: admin,
        items: [
          {
            id: 'app-catalogue-recommendations',
            label: 'Recommendations',
            path: '/admin/app-catalogue/recommendations',
            icon: <IconSparkles size={16} />,
            visible: admin,
          },
        ],
      },
      {
        id: 'app-catalogue-access',
        label: 'Access',
        visible: admin,
        items: [
          {
            id: 'app-catalogue-entitlements',
            label: 'Entitlement Preview',
            path: '/admin/app-catalogue/entitlements',
            icon: <IconShieldCheck size={16} />,
            visible: admin,
          },
          {
            id: 'app-catalogue-features',
            label: 'Features & Permissions',
            path: '/admin/app-catalogue/features',
            icon: <IconFlag size={16} />,
            visible: admin,
          },
        ],
      },
    ];

    return {
      domainId: this.domainId,
      sections,
      metadata: {
        version: '1.0.0',
        lastUpdated: Date.now(),
        description: 'App Catalogue — admin management of the CCMS mobile constellation',
      },
    };
  }
}

export const appCatalogueMenuProvider = new AppCatalogueMenuProvider();
